import React, { useState, useEffect, useCallback } from 'react';
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import BoxHeader from "../../Components/Box/BoxHeader";
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Box, TextField, Typography, MenuItem } from '@mui/material';
// import SearchableDropdown from '../../Components/SearchableDropdown/SearchableDropdown';
// import DatePicker from '../../Components/DatePicker/DatePicker';
import { useDispatch } from "react-redux";
import SelectedUserMenu from "../../Assets/images/menu-icons/manage_accounts_selected.png";
import { AntTab, AntTabs } from "../../Components/Tab/AntTab";
import { GetApiMethod } from "../../Utils/ApiService";
import { showLoader } from "../../Store/loaderSlice";
import { CustomTablePagination } from "../../Components/Pagination/CustomTablePagination";
import TableSearch from "../../Components/Search/TableSearch";

interface PlayerListDto {
  records: any[];
  totalCount: number;
}

export default function PlayerManagement() {
  const [value, setValue] = useState(0);
  const [rejectedPlayers, setRejectedPlayers] = useState<PlayerListDto>({ records: [], totalCount: 0 });
  const [acceptedPlayers, setAcceptedPlayers] = useState<PlayerListDto>({ records: [], totalCount: 0 });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [userNames, setUserNames] = useState<{[key: string]: string}>({});
  const [selectedPlayer, setSelectedPlayer] = useState("");
  const [selectedRejectedBy, setSelectedRejectedBy] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [allPlayers, setAllPlayers] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const dispatch = useDispatch();

  const fetchUserName = useCallback(async (userId: string) => {
    if (!userId || userNames[userId]) return userNames[userId] || userId;
    try {
      const response = await GetApiMethod(`user/${userId}`);
      const name = `${response.data.data.firstName} ${response.data.data.lastName}`;
      setUserNames(prev => ({ ...prev, [userId]: name }));
      return name;
    } catch (error) {
      return userId;
    }
  }, [userNames]);

  const fetchAllUsers = useCallback(async () => {
    try {
      const response = await GetApiMethod('user?page=1&limit=1000&sort=first_name&sortDirection=ASC');
      const users = response.data.data.records;
      console.log('All users:', users);
      setAllUsers(users);
      
      // Filter players - only show users with 'player' role
      const players = users.filter((user: any) => 
        user.roles?.some((role: any) => role.roleName === 'player') &&
        user.firstName && user.lastName
      );
      console.log('Filtered players:', players);
      setAllPlayers(players);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  const fetchRejectedPlayers = useCallback(async () => {
    try {
      dispatch(showLoader({ showLoader: true }));
      const params: any = {
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
      };
      
      // if (selectedPlayer) params.playerId = selectedPlayer;
      // if (selectedRejectedBy) params.rejectedById = selectedRejectedBy;
      // Date filter temporarily disabled
      // if (startDate && endDate) {
      //   params.startDate = new Date(startDate).toISOString();
      //   params.endDate = new Date(endDate + 'T23:59:59').toISOString();
      // } else if (selectedDate) {
      //   const date = new Date(selectedDate);
      //   const startOfDay = new Date(date.setHours(0, 0, 0, 0)).toISOString();
      //   const endOfDay = new Date(date.setHours(23, 59, 59, 999)).toISOString();
      //   params.startDate = startOfDay;
      //   params.endDate = endOfDay;
      // }
      
      const queryParams = new URLSearchParams(params).toString();
      const response = await GetApiMethod('user/player-rejections?' + queryParams);
      setRejectedPlayers({
        records: response.data.data.records,
        totalCount: response.data.data.totalCount
      });
      dispatch(showLoader({ showLoader: false }));
    } catch (error) {
      console.error('Error fetching rejected players:', error);
      dispatch(showLoader({ showLoader: false }));
    }
  }, [dispatch, page, rowsPerPage, selectedPlayer, selectedRejectedBy, selectedDate, startDate, endDate]);

  const fetchAcceptedPlayers = useCallback(async () => {
    try {
      dispatch(showLoader({ showLoader: true }));
      const params: any = {
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
      };
      
      // if (selectedPlayer) params.playerId = selectedPlayer;
      // if (selectedRejectedBy) params.acceptedById = selectedRejectedBy;
      // Date filter temporarily disabled
      // if (startDate && endDate) {
      //   params.startDate = new Date(startDate).toISOString();
      //   params.endDate = new Date(endDate + 'T23:59:59').toISOString();
      // } else if (selectedDate) {
      //   const date = new Date(selectedDate);
      //   const startOfDay = new Date(date.setHours(0, 0, 0, 0)).toISOString();
      //   const endOfDay = new Date(date.setHours(23, 59, 59, 999)).toISOString();
      //   params.startDate = startOfDay;
      //   params.endDate = endOfDay;
      // }
      
      const queryParams = new URLSearchParams(params).toString();
      const response = await GetApiMethod('user/player-accepts?' + queryParams);
      const records = response.data.data.records;
      
      // Fetch user names for all unique user IDs
      const userIds = [...new Set([...records.map((r: any) => r.requestFrom), ...records.map((r: any) => r.userId)])].filter(Boolean);
      await Promise.all(userIds.map((id: string) => fetchUserName(id)));
      
      setAcceptedPlayers({
        records,
        totalCount: response.data.data.totalCount
      });
      dispatch(showLoader({ showLoader: false }));
    } catch (error) {
      console.error('Error fetching accepted players:', error);
      dispatch(showLoader({ showLoader: false }));
    }
  }, [dispatch, page, rowsPerPage, fetchUserName, selectedPlayer, selectedRejectedBy, selectedDate, startDate, endDate]);

  useEffect(() => {
    fetchAllUsers();
  }, [fetchAllUsers]);

 useEffect(() => {
  if (value === 0) {
    fetchRejectedPlayers();
  } else {
    fetchAcceptedPlayers();
  }
}, [
  value,
  page,
  rowsPerPage,
  selectedPlayer,
  selectedRejectedBy,
  selectedDate,
  startDate,
  endDate,
  fetchRejectedPlayers,
  fetchAcceptedPlayers
]);


  return (
    <AuthorizedLayout>
      <BoxHeader
        dashboard_name="Player Management"
        image={SelectedUserMenu}
        button_show={false}
      >
        <AntTabs value={value} onChange={(_, newValue) => setValue(newValue)}>
          <AntTab label="Rejected Players" className="tab-menu" />
          <AntTab label="Accepted Players" className="tab-menu" />
        </AntTabs>
      </BoxHeader>

      {/* <Box display="flex" alignItems="center" gap={2} mt={2} flexWrap="wrap">
        <SearchableDropdown
          label="Filter by Player:"
          options={[
            { value: '', label: 'All Players' },
            ...allPlayers.map(player => ({
              value: player.id,
              label: `${player.firstName || 'Unknown'} ${player.lastName || 'User'}`
            }))
          ]}
          value={selectedPlayer}
          onSelect={(option) => setSelectedPlayer(option?.value || '')}
          placeholder="Search players..."
          maxHeight="200px"
        />
        <SearchableDropdown
          label={value === 0 ? "Filter by Rejected By:" : "Filter by Accepted By:"}
          options={[
            { value: '', label: value === 0 ? 'All Rejectors' : 'All Acceptors' },
            ...allUsers.map(user => ({
              value: user.id,
              label: `${user.firstName || 'Unknown'} ${user.lastName || 'User'}`
            }))
          ]}
          value={selectedRejectedBy}
          onSelect={(option) => setSelectedRejectedBy(option?.value || '')}
          placeholder="Search users..."
          maxHeight="200px"
        />
        <DatePicker
          label={value === 0 ? "Rejected At:" : "Accepted At:"}
          value={selectedDate}
          onChange={setSelectedDate}
          startDate={startDate}
          endDate={endDate}
          onRangeChange={(start, end) => {
            setStartDate(start);
            setEndDate(end);
            setSelectedDate('');
          }}
        />
      </Box> */}

      {value === 0 && (
        <>
          <TableContainer className="mt-2">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User Name</TableCell>
                  <TableCell>Rejected By</TableCell>
                  <TableCell>Rejected At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rejectedPlayers.records.map((player: any) => (
                  <TableRow key={player.id}>
                    <TableCell>{player.player?.firstName} {player.player?.lastName}</TableCell>
                    <TableCell>{player.rejectedBy?.firstName} {player.rejectedBy?.lastName}</TableCell>
                    <TableCell>{new Date(player.createdAt).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
                {rejectedPlayers.records.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No rejected players found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={rejectedPlayers.totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}

      {value === 1 && (
        <>
          <TableContainer className="mt-2">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User Name</TableCell>
                  <TableCell>Accepted By</TableCell>
                  <TableCell>Accepted At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {acceptedPlayers.records.map((player: any) => (
                  <TableRow key={player.id}>
                    <TableCell>{userNames[player.requestFrom] || player.requestFrom}</TableCell>
                    <TableCell>{userNames[player.userId] || player.userId}</TableCell>
                    <TableCell>{new Date(player.createdAt).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
                {acceptedPlayers.records.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No accepted players found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={acceptedPlayers.totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}
    </AuthorizedLayout>
  );
}